import React from 'react'
import { FormGroup, Input, Label, Button, Alert } from 'reactstrap'
import { ValidationBoundary, Validator, ValidationState } from '@/components/validation'
import { ServerErrorViewer } from '@/components/validation/serverErrorViewer'
import Resx from '@/components/Resx'
import isEnterPressed from '@/utils/isEnterPressed'
import { format } from '@/utils/StringUtilities'
import { formatDate } from '@/utils/dateUtilities'
import { openTwoFactorDialog } from '@/utils/twoFactorHelper'
import moment from 'moment'
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect'
import context from '@/context'
import { allowErrorDialog } from '@/utils/fetch'
import { openAlertDialog } from '@/utils/dialogHelpers'

const ChangePasswordForm = (props) => {
  const [ currentPassword, setCurrentPassword ] = React.useState('')
  const [ newPassword, setNewPassword ] = React.useState('')
  const [ repeatNewPassword, setRepeatNewPassword ] = React.useState('')
  const [ error, setError ] = React.useState(props.error)
  const [ customError, setCustomCustomError ] = React.useState({})

  useDebouncedEffect(() => {
    if (!newPassword && !repeatNewPassword) {
      setCustomCustomError(null)
      return
    }
    let errors = []
    if (newPassword !== repeatNewPassword) {
      errors.push({ localizedMessage: props.localize('Security.PassphrasesDiffer') })
    }
    context.api.userProfile.verifyPassword({ newPassword }, allowErrorDialog)
      .then(response => {
        if (response.validationErrors) { errors.push(...response.validationErrors) }
        setCustomCustomError(errors.length > 0 ? { validationErrors: errors } : null)
      }).catch(err => {
        handleError(errors, err)
      })
  }, [ newPassword, repeatNewPassword ], 300)

  const handleError = (errors, err) => {
    errors.push({ localizedMessage: props.localize('Common.Error') })
    setCustomCustomError(errors.length > 0 ? { validationErrors: errors } : null)
    console.error(err)
  }

  const handleSaveButtonClick = () => {
    if (isInputOk()) {
      openTwoFactorDialog(() => savePasswordCallback())
    }
  }

  const savePasswordCallback = () => {
    context.api.userProfile.changePassword({ currentPassword, newPassword }, allowErrorDialog)
      .then(result => {
        let changePasswordError = null
        if (result.success === true) {
          openAlertDialog({ title: 'Security.ChangePassword', text: result.message })
          props.getPasswordValidity()
        } else {
          changePasswordError = {
            localizedMessage: result.message,
            validationErrors: result.validationErrors.map(msg => ({
              members: ['newPassword', 'repeatNewPassword'],
              localizedMessage: msg
            }))
          }
        }
        setError(changePasswordError)
      })
      .catch((err) => handleError([], err))
  }

  const isInputOk = () => {
    if (newPassword !== repeatNewPassword) {
      setError({ message: 'Security.PasswordsDiffer' })
    } else {
      return true
    }
    return false
  }
  const handleKeyDown = (e) => {
    if (isEnterPressed(e)) {
      handleSaveButtonClick()
    }
  }
  const renderPasswordValidity = () => {
    const { passwordValidity, localize } = props
    if (passwordValidity) {
      const daysToExpiration = moment.duration(moment(passwordValidity).diff(moment())).asDays()
      const alertColor = daysToExpiration > 7 ? 'info' : 'warning'
      return (
        <FormGroup>
          <Alert color={alertColor}>
            { format(localize('Security.CurrentPasswordValidity'), formatDate(passwordValidity)) }
          </Alert>
        </FormGroup>
      )
    }
    return null
  }

  const mergeErrors = () => {
    if (!customError || customError === {}) {
      return error
    } else if (!error || error === {}) {
      return customError
    }
    const compiledError = { ...customError }
    for (const validationError in error.validationErrors) {
      const message = error.validationErrors[validationError].localizedMessage
      if (compiledError.validationErrors.some(ve => ve.localizedMessage === message)) { continue }

      compiledError.validationErrors.push({ localizedMessage: message })
    }
    return compiledError
  }

  return (
    <ValidationBoundary serverError={error}>
      <React.Fragment>
        { renderPasswordValidity() }
        <FormGroup>
          <Label style={{ display: 'block' }}><Resx name='Security.CurrentPassword' /></Label>
          <Validator name='currentPassword' value={currentPassword} required>
            <Input type='password' value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)} />
          </Validator>
        </FormGroup>
        <FormGroup>
          <Label style={{ display: 'block' }}><Resx name='Security.NewPassword' /></Label>
          <Validator name='newPassword' value={newPassword} required>
            <Input type='password' value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)} />
          </Validator>
        </FormGroup>
        <FormGroup>
          <Label style={{ display: 'block' }}><Resx name='Security.RepeatNewPassword' /></Label>
          <Validator name='repeatNewPassword' value={repeatNewPassword} required>
            <Input type='password' value={repeatNewPassword} onKeyDown={handleKeyDown}
              onChange={(e) => setRepeatNewPassword(e.target.value)} />
          </Validator>
        </FormGroup>
        <ValidationState render={({ isValid }) =>
          <React.Fragment>
            <ServerErrorViewer error={mergeErrors()} showErrorMessage />
            <div className='text-right'>
              <Button color='success' className='save-button' onClick={() => handleSaveButtonClick()}
                disabled={!isValid}><Resx name='Common.Save' /></Button>
            </div>
          </React.Fragment>
        } />
      </React.Fragment>
    </ValidationBoundary>
  )
}

export default ChangePasswordForm



// WEBPACK FOOTER //
// ./src/components/userProfile/changePassword/form.js